import { State } from './StateMachine';
import { WebGLRenderer } from '../renderer/WebGLRenderer';
import { InputManager } from '../core/InputManager';
import { TextRenderer } from '../ui/TextRenderer';
import { HighScoreManager } from '../core/HighScoreManager';
import { CANVAS_WIDTH } from '../utils/constants';

// Helper to center text (8px per character)
function centerX(text: string): number {
  return (CANVAS_WIDTH - text.length * 8) / 2;
}

export class HighScoresState implements State {
  private renderer: WebGLRenderer;
  private input: InputManager;
  private textRenderer: TextRenderer;
  private highScoreManager: HighScoreManager;
  private onBack: () => void;
  private displayTimer: number = 0;
  private autoReturn: boolean = false;
  private autoReturnDelay: number = 8000; // 8 seconds

  constructor(
    renderer: WebGLRenderer,
    input: InputManager,
    textRenderer: TextRenderer,
    highScoreManager: HighScoreManager,
    onBack: () => void
  ) {
    this.renderer = renderer;
    this.input = input;
    this.textRenderer = textRenderer;
    this.highScoreManager = highScoreManager;
    this.onBack = onBack;
  }

  /**
   * Enable auto-return mode (returns to menu after delay)
   */
  setAutoReturn(enabled: boolean, delay: number = 8000): void {
    this.autoReturn = enabled;
    this.autoReturnDelay = delay;
  }

  enter(): void {
    this.displayTimer = 0;
    // Refresh scores when entering
    this.highScoreManager.refreshIfNeeded();
  }

  handleInput(): void {
    if (
      this.input.isKeyDown('Space') ||
      this.input.isKeyDown('Enter') ||
      this.input.isKeyDown('Escape')
    ) {
      this.onBack();
    }
  }

  update(dt: number): void {
    this.displayTimer += dt;

    // Auto-return to menu after delay
    if (this.autoReturn && this.displayTimer >= this.autoReturnDelay) {
      this.onBack();
    }
  }

  render(): void {
    this.renderer.clear();
    this.renderer.beginText();

    // Title
    const title = 'HIGH SCORES';
    this.textRenderer.draw(title, centerX(title), 30, 1, 1, 0); // Yellow

    const scores = this.highScoreManager.getScores();
    const error = this.highScoreManager.getError();
    const submittedId = this.highScoreManager.getSubmittedScoreId();

    if (this.highScoreManager.isLoading()) {
      const loadingText = 'LOADING';
      this.textRenderer.draw(loadingText, centerX(loadingText), 140);
    } else if (error) {
      this.textRenderer.draw(error, centerX(error), 140, 1, 0, 0); // Red
    } else if (scores.length === 0) {
      const noScores = 'NO SCORES YET';
      this.textRenderer.draw(noScores, centerX(noScores), 140);
      const beFirst = 'BE THE FIRST';
      this.textRenderer.draw(beFirst, centerX(beFirst), 160);
    } else {
      // Column headers
      const headerY = 60;
      this.textRenderer.draw('RANK', 24, headerY, 0.5, 0.5, 0.5);
      this.textRenderer.draw('NAME', 72, headerY, 0.5, 0.5, 0.5);
      this.textRenderer.draw('SCORE', 152, headerY, 0.5, 0.5, 0.5);

      // Draw scores
      const startY = 80;
      const lineHeight = 16;

      scores.forEach((score, index) => {
        const y = startY + index * lineHeight;
        const isSubmitted = score.id === submittedId;

        // Rank
        const rankText = `${index + 1}.`.padStart(3, ' ');

        // Name (truncate if too long)
        const name = score.playerName.substring(0, 10).padEnd(10, ' ');

        // Score
        const scoreText = score.score.toString().padStart(8, ' ');

        if (isSubmitted) {
          // Highlight the just-submitted score in cyan
          this.textRenderer.draw(rankText, 24, y, 0, 1, 1);
          this.textRenderer.draw(name, 72, y, 0, 1, 1);
          this.textRenderer.draw(scoreText, 152, y, 0, 1, 1);
        } else {
          this.textRenderer.draw(rankText, 24, y);
          this.textRenderer.draw(name, 72, y);
          this.textRenderer.draw(scoreText, 152, y);
        }
      });
    }

    // Instructions at bottom
    const pressKey = 'PRESS SPACE TO CONTINUE';
    this.textRenderer.draw(pressKey, centerX(pressKey), 280);

    this.renderer.endBatch();
  }
}
